'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { Heading, type HeadingData } from '@/components/cms/Heading';
import { FreeTextContent } from '@/components/cms/FreeTextContent';
import { LinkButton } from '@/components/ui/Button/LinkButton';
import { Icon } from '@/components/ui/Icon';
import { BRAND_NAME } from '@/lib/const';
import { getCountryFromLocale } from '@/lib/i18n/config';
import { boutiquePaths } from '@/lib/paths';
import { resolveServiceRequestChannels } from '@/lib/service/resolveServiceRequestChannels';

import styles from './PlaceholderServiceServiceRequest.module.scss';
import { ServiceRequestBoutiqueIcon, ServiceRequestSendIcon } from './ServiceRequestIcons';

export interface PlaceholderServiceServiceRequestProps {
  locale?: string;
  placeholderHeading?: HeadingData | null;
  subtext?: { json?: unknown } | null;
  ecsAvailable?: boolean;
  serviceRequestEnabled?: boolean;
}

const SERVICE_REQUEST_PATH = '/service/request';

export function PlaceholderServiceServiceRequest({
  locale,
  placeholderHeading,
  subtext,
  ecsAvailable = false,
  serviceRequestEnabled = false,
}: Readonly<PlaceholderServiceServiceRequestProps>) {
  const t = useTranslations('service.repairRequest.cmsChannels');

  const { showSend, showBoutique } = useMemo(
    () =>
      resolveServiceRequestChannels({
        ecsAvailable,
        serviceRequestEnabled,
      }),
    [ecsAvailable, serviceRequestEnabled]
  );

  const countryCode = locale ? getCountryFromLocale(locale).toUpperCase() : undefined;
  const boutiqueHref = boutiquePaths.listing(countryCode);

  if (!showSend && !showBoutique) {
    return null;
  }

  const hasHeader = placeholderHeading != null || subtext?.json != null;

  return (
    <section className={styles.section} data-testid="PlaceholderServiceServiceRequest">
      <div className={styles.inner}>
        {hasHeader ? (
          <div className={styles.header}>
            {placeholderHeading ? <Heading Heading={placeholderHeading} /> : null}
            {subtext?.json ? (
              <FreeTextContent className={styles.intro} freeTextContent={subtext} />
            ) : null}
          </div>
        ) : null}

        <div
          className={clsx(styles.columns, !showSend && styles.columnsSingle)}
          data-testid="ServiceRequestColumns"
        >
          {showSend ? (
            <div className={styles.column} data-testid="ServiceRequestSendColumn">
              <span className={styles.icon}>
                <ServiceRequestSendIcon />
              </span>
              <h3 className={styles.title}>{t('sendTitle')}</h3>
              <p className={styles.description}>{t('sendDescription', { brand: BRAND_NAME })}</p>
              <LinkButton
                href={SERVICE_REQUEST_PATH}
                variant="secondary"
                size="md"
                className={styles.cta}
                iconAfter={<Icon aria-hidden="true" name="chevron-right" size={14} />}
                data-testid="ServiceRequestRequestCta"
              >
                {t('requestCta')}
              </LinkButton>
            </div>
          ) : null}

          {showBoutique ? (
            <div className={styles.column} data-testid="ServiceRequestBoutiqueColumn">
              <span className={styles.icon}>
                <ServiceRequestBoutiqueIcon />
              </span>
              <h3 className={styles.title}>{t('dropOffTitle')}</h3>
              <p className={styles.description}>{t('dropOffDescription', { brand: BRAND_NAME })}</p>
              <LinkButton
                href={boutiqueHref}
                variant="secondary"
                size="md"
                className={styles.cta}
                iconAfter={<Icon aria-hidden="true" name="chevron-right" size={14} />}
                data-testid="ServiceRequestBoutiqueCta"
              >
                {t('findBoutiqueCta')}
              </LinkButton>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

PlaceholderServiceServiceRequest.displayName = 'PlaceholderServiceServiceRequest';
